import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DetailsPanelShellComponent } from 'src/app/components/details-panel-shell/details-panel-shell.component';
import { StatusPillComponent, PillStatusConfig } from 'src/app/components/status-pill/status-pill.component';
import { ContractsPillComponent } from 'src/app/components/contracts-pill/contracts-pill.component';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { ContractStatusConfig } from 'src/app/constants/status-pill-configs';
import { ContractService } from 'src/app/services/contracts/contract.service';
import { ConfigService } from 'src/app/services/config.service';
import { CalendarEntry } from '../calendar-month-panel/calendar.types';

// Ticket #4549: side panel shown when a contract-deadline entry is clicked on the project
// calendar. The flattened deadline row already carries deadline-level fields (note, done,
// responsible user, contract type/parties), but not the contract's own status or its
// documents — those need one extra fetch of the full contract record.
@Component({
    selector: 'app-deadline-detail-panel',
    imports: [CommonModule, DetailsPanelShellComponent, StatusPillComponent, ContractsPillComponent, GpdButtonComponent],
    templateUrl: './deadline-detail-panel.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../details-panel-shell/panel-content.css', './deadline-detail-panel.component.css']
})
export class DeadlineDetailPanelComponent implements OnChanges {
  @Input() visible = false;
  @Input() entry: CalendarEntry | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onOpenContract = new EventEmitter<void>();

  readonly contractStatusConfig = ContractStatusConfig;
  readonly deadlineStatusConfig: PillStatusConfig[] = [
    { id: 0, status: $localize`:@@open:Open`, textColor: '#0369a1', color: '#e0f2fe', border: '1px solid #7dd3fc' },
    { id: 1, status: $localize`:@@done:Done`, textColor: '#ffffff', color: '#629D37', border: '1px solid #629D37' },
    { id: 2, status: $localize`:@@overdue:Overdue`, textColor: '#ffffff', color: '#ea3f3f', border: '1px solid #ea3f3f' },
  ];

  contract: any = null;
  loadingContract = false;

  readonly labelDue = $localize`:@@calendarDueDate:Due`;
  readonly labelDeadlineDetails = $localize`:@@deadlineDetailsSection:Deadline details`;
  readonly labelDeadlineType = $localize`:@@deadlineType:Deadline Type`;
  readonly labelAssignedTo = $localize`:@@assignedTo:Assigned to`;
  readonly labelStatus = $localize`:@@status:Status`;
  readonly labelNote = $localize`:@@note:Note`;
  readonly labelContractDetails = $localize`:@@contractDetailsSection:Contract details`;
  readonly labelContractName = $localize`:@@calendarContractName:Contract name`;
  readonly labelContractType = $localize`:@@contractType:Contract Type`;
  readonly labelIntern = $localize`:@@intern:Intern`;
  readonly labelExtern = $localize`:@@extern:Extern`;
  readonly labelOpenContract = $localize`:@@calendarOpenContract:Open contract`;
  readonly labelDocuments = $localize`:@@documents:Documents`;

  private userMap: { [email: string]: string } = {};

  constructor(private contractService: ContractService, private configService: ConfigService) {
    this.configService.loadUserList();
    this.configService.userMap$.subscribe((map) => (this.userMap = map));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entry'] && this.entry?.type === 'deadline') {
      this.loadContract();
    }
  }

  get deadline(): any {
    return this.entry?.source ?? null;
  }

  /** Resolves the assignee's email to a display name (e.g. "Rossella Filocomo") when known. */
  get assigneeDisplayName(): string | null {
    // #4994: a department holds it; responsibleUser then already carries the department name
    if (this.deadline?.responsibleDepartmentId != null)
      return this.deadline.responsibleDepartmentName || this.deadline.responsibleUser || null;
    const email = this.deadline?.responsibleUser || this.deadline?.responsibleUserMail;
    if (!email) return null;
    return this.userMap[email] || email;
  }

  get contractTypeLabel(): string {
    return this.contractService.getContractTypeById(this.deadline?.contractTypeId);
  }

  get deadlineTypeLabel(): string {
    return this.contractService.getContractDeadlineTypeById(this.deadline?.deadlineTypeId);
  }

  /** 1 = Erledigt (done checkbox is checked), 2 = Überfällig (past due date, not done), 0 = Offen. */
  get deadlineStatusId(): number {
    if (this.deadline?.done) return 1;
    if (this.entry?.date && this.entry.date < this.startOfToday()) return 2;
    return 0;
  }

  formatDueDate(date: Date): string {
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'long' }).format(date);
  }

  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private loadContract(): void {
    const contractId = this.deadline?.contractId;
    if (!contractId) {
      this.contract = null;
      return;
    }
    this.loadingContract = true;
    this.contractService.getContractById(contractId).then((contract) => {
      this.loadingContract = false;
      this.contract = contract;
    });
  }
}
